import { CommonModule } from '@angular/common';
import { Component, OnDestroy, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { Subject, takeUntil } from 'rxjs';
import { CityParkingCustomerInfoResponse } from '../../../../../core/models/customer.model';
import {
  CustomerDashboardStat,
  CustomerRecentActivityItem,
} from '../../../../../core/models/customer/customer-management.model';
import { ModalCustomerComponent } from '../../../modals/customer/modal-customer/modal-customer.component';
import { ApiCustomerManagementService } from '../../../../../core/services/api/customer/api-customer-management.service';
import { MapperCustomerManagementService } from '../../../../../core/mappers/customer/mapper-customer-management.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { ApiService } from '../../../../../core/services/api.service';
import { ClientIInfo } from '../../../../../core/models/parking.model';

type CustomerSearchType = 'name' | 'phone' | 'email' | 'client';
type CustomerStatusFilter = 'all' | 'active' | 'inactive';

const CLIENT_DROPDOWN_LIMIT = 50;

@Component({
  selector: 'app-customer-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalCustomerComponent],
  templateUrl: './customer-management.component.html',
  styleUrls: ['./customer-management.component.css'],
})
export class CustomerManagementComponent implements OnDestroy {
  private readonly apiService = inject(ApiCustomerManagementService);
  private readonly mapper = inject(MapperCustomerManagementService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly apiLegacy = inject(ApiService);
  readonly siteState = inject(SiteStateService);
  private readonly destroy$ = new Subject<void>();

  readonly searchTypeOptions: { value: CustomerSearchType; label: string; icon: string; placeholder: string }[] = [
    { value: 'name', label: 'ชื่อลูกค้า', icon: 'person', placeholder: 'ค้นหาชื่อลูกค้า...' },
    { value: 'phone', label: 'เบอร์โทรศัพท์', icon: 'phone', placeholder: 'ค้นหาเบอร์โทร...' },
    { value: 'email', label: 'อีเมล', icon: 'mail', placeholder: 'ค้นหาอีเมล...' },
    { value: 'client', label: 'บริษัทผู้เช่า', icon: 'business_center', placeholder: 'ค้นหาบริษัทผู้เช่า...' },
  ];

  readonly statTones = [
    'bg-sky-50 text-sky-500 dark:bg-sky-900/30 dark:text-sky-400',
    'bg-amber-50 text-amber-500 dark:bg-amber-900/30 dark:text-amber-400',
    'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400',
    'bg-rose-50 text-rose-500 dark:bg-rose-900/30 dark:text-rose-400',
  ];

  searchText = '';
  searchType: CustomerSearchType = 'name';
  isSearchTypeDropdownOpen = false;
  filterStatus: CustomerStatusFilter = 'all';

  filterClientId: number | null = null;
  filterUnassigned = false;
  filterClients: ClientIInfo[] = [];
  isLoadingFilterClients = false;
  clientFilterText = '';
  isClientDropdownOpen = false;

  currentPage = 1;
  pageSize = 20;
  readonly pageSizeOptions: number[] = [20, 50, 100];
  isPageSizeDropdownOpen = false;
  totalItems = 0;
  totalPages = 1;

  customers: CityParkingCustomerInfoResponse[] = [];
  isLoading = false;

  isModalOpen = false;
  selectedCustomer: CityParkingCustomerInfoResponse | null = null;

  dashboardStats: CustomerDashboardStat[] = this.mapper.getEmptyDashboardStats();
  recentActivity: CustomerRecentActivityItem[] = [];
  newPercent = 0;
  oldPercent = 0;

  showDashboard = true;
  showTableFilters = false;

  private customerRequestSeq = 0;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      untracked(() => {
        this.closeModal();
        this.resetFilters();
        this.filterClients = [];
        if (!site?.id) {
          this.clearTable();
          return;
        }
        this.loadFilterClients(site.id);
        this.loadCustomers();
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get currentSearchType() {
    return this.searchTypeOptions.find((o) => o.value === this.searchType) ?? this.searchTypeOptions[0];
  }

  get hasAdvancedFilter(): boolean {
    return this.filterUnassigned || !!this.filterClientId;
  }

  get selectedFilterClientName(): string {
    if (!this.filterClientId) return '';
    return this.filterClients.find((c) => c.id === this.filterClientId)?.name ?? '';
  }

  get filteredFilterClients(): ClientIInfo[] {
    const term = this.clientFilterText.trim().toLowerCase();
    const selectedName = this.selectedFilterClientName.toLowerCase();
    const list = !term || term === selectedName
      ? this.filterClients
      : this.filterClients.filter((c) => (c.name ?? '').toLowerCase().includes(term));
    return list.slice(0, CLIENT_DROPDOWN_LIMIT);
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  /* ---------------------------- Filters ---------------------------- */

  onRefresh(): void {
    this.resetFilters();
    this.loadCustomers();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadCustomers();
  }

  selectStatus(status: CustomerStatusFilter): void {
    if (this.filterStatus === status) return;
    this.filterStatus = status;
    this.currentPage = 1;
    this.loadCustomers();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  closeSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = false;
  }

  selectSearchType(type: CustomerSearchType): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    if (this.searchText.trim()) {
      this.onSearch();
    }
  }

  openClientDropdown(): void {
    if (!this.filterClients.length) return;
    this.isClientDropdownOpen = true;
  }

  onClientFilterBlur(): void {
    this.isClientDropdownOpen = false;
    this.clientFilterText = this.selectedFilterClientName;
  }

  selectFilterClient(client: ClientIInfo): void {
    this.filterClientId = client.id;
    this.clientFilterText = client.name ?? '';
    this.isClientDropdownOpen = false;
    this.filterUnassigned = false;
    this.currentPage = 1;
    this.loadCustomers();
  }

  clearFilterClient(): void {
    if (!this.filterClientId && !this.clientFilterText) return;
    this.filterClientId = null;
    this.clientFilterText = '';
    this.currentPage = 1;
    this.loadCustomers();
  }

  toggleUnassignedFilter(): void {
    this.filterUnassigned = !this.filterUnassigned;
    if (this.filterUnassigned) {
      this.filterClientId = null;
      this.clientFilterText = '';
    }
    this.currentPage = 1;
    this.loadCustomers();
  }

  clearAdvancedFilters(): void {
    this.filterClientId = null;
    this.clientFilterText = '';
    this.filterUnassigned = false;
    this.currentPage = 1;
    this.loadCustomers();
  }

  /* ---------------------------- Pagination ---------------------------- */

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadCustomers();
    }
  }

  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  closePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = false;
  }

  selectPageSize(size: number): void {
    this.isPageSizeDropdownOpen = false;
    if (size === this.pageSize) return;
    this.pageSize = size;
    this.currentPage = 1;
    this.loadCustomers();
  }

  /* ---------------------------- Modal ---------------------------- */

  openAddModal(): void {
    if (!this.selectedSiteId) {
      this.toast.warn('กรุณาเลือกลานจากแถบด้านข้างก่อน');
      return;
    }
    this.selectedCustomer = null;
    this.isModalOpen = true;
  }

  openEditModal(customer: CityParkingCustomerInfoResponse): void {
    this.selectedCustomer = customer;
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selectedCustomer = null;
  }

  onSaveSuccess(): void {
    this.loadCustomers();
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  isCustomerUnlinked(m: CityParkingCustomerInfoResponse): boolean {
    return !m.clientId;
  }

  customerInitial(name: string | undefined): string {
    return (name || '?').trim().charAt(0).toUpperCase() || '?';
  }

  /* ---------------------------- Data ---------------------------- */

  loadCustomers(): void {
    const siteId = this.selectedSiteId;
    if (!this.cookieService.get('token') || !siteId) return;

    const seq = ++this.customerRequestSeq;
    this.isLoading = true;

    const trimmedSearch = this.searchText.trim();
    const searchField = trimmedSearch ? this.mapper.mapSearchField(this.searchType) : undefined;
    const isFirstPage = this.currentPage === 1;

    this.apiService.getCityParkingCustomerForTable({
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField,
      searchText: trimmedSearch || undefined,
      status: this.filterStatus,
      siteId,
      clientId: this.filterClientId ?? undefined,
      unassigned: this.filterUnassigned,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (seq !== this.customerRequestSeq) return;
        const mapped = this.mapper.mapTableFromDto(res);
        this.customers = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = Math.max(mapped.totalPages || 1, 1);

        if (isFirstPage) {
          this.dashboardStats = mapped.dashboardStats;
          this.newPercent = mapped.newPercent;
          this.oldPercent = mapped.oldPercent;
          this.recentActivity = mapped.recentActivity;
        }

        this.isLoading = false;
      },
      error: (err) => {
        if (seq !== this.customerRequestSeq) return;
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูลลูกค้าได้');
        this.isLoading = false;
      },
    });
  }

  private loadFilterClients(siteId: number): void {
    this.isLoadingFilterClients = true;
    this.apiLegacy.GetClientIInfo(siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (this.selectedSiteId !== siteId) return;
        this.filterClients = [...(res ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'th'));
        this.isLoadingFilterClients = false;
      },
      error: () => {
        if (this.selectedSiteId !== siteId) return;
        this.filterClients = [];
        this.isLoadingFilterClients = false;
      },
    });
  }

  private resetFilters(): void {
    this.searchText = '';
    this.searchType = 'name';
    this.filterStatus = 'all';
    this.filterClientId = null;
    this.clientFilterText = '';
    this.filterUnassigned = false;
    this.isClientDropdownOpen = false;
    this.isSearchTypeDropdownOpen = false;
    this.currentPage = 1;
  }

  private clearTable(): void {
    this.customerRequestSeq++;
    this.customers = [];
    this.totalItems = 0;
    this.totalPages = 1;
    this.isLoading = false;
    this.dashboardStats = this.mapper.getEmptyDashboardStats();
    this.recentActivity = [];
    this.newPercent = 0;
    this.oldPercent = 0;
  }
}
